/*
The /calendar/releases/ page: view switch (timeline | month), type filter with counts,
month arrows, and the calendar itself. State lives in the address
(?view=timeline&type=text&month=2026-09) so a view can be shared by link. One details
window serves all items (calendarPopover). Needs page-releases.hbs.
On a phone (releasesPhone.PHONE) the month view is the days list (releasesDays), the controls
are the bottom dock, the type sheet and the month select; the page is drawn again when the
screen crosses that width.
*/
import afterLoad from './afterLoad';
import { bindPopover } from './calendarPopover';
import { element } from './eventsData';
import {
    TYPES,
    capitalize,
    countByType,
    fetchReleases,
    isMonthKey,
    dayGroups,
    monthKey,
    monthLabel,
    shiftMonth,
    todayKey,
} from './releasesData';
import { buildDays, daysSpan, monthStart } from './releasesDays';
import { buildMonth } from './releasesMonth';
import {
    PHONE,
    bindTypeSheet,
    closeTypeSheet,
    fillMonthSelect,
    scrollPageTo,
    stuckHeaderHeight,
} from './releasesPhone';

import {
    buildTimeline,
    cellChips,
    scrollToMonth,
    scrollToMonthEnd,
    timelineMonths,
} from './releasesTimeline';
import { expectedDetails, modelChip, releaseDetails } from './releasesView';

const VIEWS = ['timeline', 'month'];
const EMPTY_NOTE = 'No releases of this type in these months.';
const ERROR_NOTE = 'The releases calendar is not available right now. Try again in a few minutes.';
// Days view: a day the page scrolls to stops this far below the site header.
const MONTH_TOP_GAP = 16;
const pages = new WeakMap();
// Data older than this is fetched again when the reader comes back to the page.
const STALE_MS = 60 * 60 * 1000;
let watchingWidth = false;

export default function releasesPage() {
    const root = document.querySelector('[data-releases-page]');
    if (!root) {
        return;
    }
    if (pages.has(root)) {
        refreshAfterReturn(pages.get(root));
        return;
    }
    const page = { root, now: new Date(), data: null, state: null };
    pages.set(root, page);
    afterLoad(() => start(page));
}

async function start(page) {
    page.root.querySelector('[data-releases-loading]')?.removeAttribute('hidden');
    // Back to this page through PJAX: the scroll it had, restored once the list is built
    // (pjax.js scrolls before the calendar has its height).
    page.restoreY = window.history.state?.thScrollY;
    const target = isMonthKey(new URLSearchParams(window.location.search).get('month')) ? 'month' : 'today';
    page.state = readState(page.now);
    writeState(page.state, page.now);
    bindPopover(page.root, (item) => details(page, item.dataset.pop));
    bindTypeSheet(page.root);
    page.root.addEventListener('click', (event) => onControl(page, event));
    page.root.addEventListener('change', (event) => onMonthSelect(page, event));
    if (await load(page)) {
        placeView(page, target);
    }
}

/**
 * Fetch the calendar and draw it; false — not shown (error note, or the page left meanwhile).
 * One request at a time; the view, type and month the reader chose stay as they are.
 */
async function load(page) {
    if (page.loading) {
        return false;
    }
    page.loading = true;
    try {
        page.data = await fetchReleases();
        page.loadedAt = Date.now();
    } catch (error) {
        showNote(page, ERROR_NOTE, error);
        return false;
    } finally {
        page.loading = false;
    }
    if (!page.root.isConnected) {
        return false;
    }
    buildControls(page);
    page.root.dataset.ready = 'true';
    render(page);
    // Here, not in start: after a failed first load a later successful one must watch too.
    watchWidth();
    return true;
}

/** The address, checked: an unknown view, type or month falls back to the default. */
function readState(now) {
    const params = new URLSearchParams(window.location.search);
    return {
        view: VIEWS.includes(params.get('view')) ? params.get('view') : 'timeline',
        type: TYPES.includes(params.get('type')) ? params.get('type') : 'text',
        month: isMonthKey(params.get('month')) ? params.get('month') : monthKey(now),
    };
}

function writeState(state, now) {
    const params = new URLSearchParams();
    if (state.view !== 'timeline') {
        params.set('view', state.view);
    }
    if (state.type !== 'text') {
        params.set('type', state.type);
    }
    if (state.month !== monthKey(now)) {
        params.set('month', state.month);
    }
    const query = params.toString();
    const address = `${window.location.pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState(window.history.state, '', address);
}

function onControl(page, event) {
    if (!page.data) {
        return;
    }
    page.now = new Date();
    if (event.target.closest('[data-go-today]')) {
        showMonth(page, monthKey(page.now), 'today');
        return;
    }
    const control = event.target.closest('[data-set]');
    if (!control) {
        return;
    }
    const [field, value] = control.dataset.set.split(':');
    if (field === 'month') {
        showMonth(page, nextMonth(page, Number(value)), 'month');
        return;
    }
    page.state = { ...page.state, [field]: value };
    writeState(page.state, page.now);
    closeTypeSheet(page.root);
    render(page);
    placeView(page, defaultTarget(page));
}

function onMonthSelect(page, event) {
    if (!page.data || !event.target.matches('[data-releases-month-select]')) {
        return;
    }
    page.now = new Date();
    const month = event.target.value;
    if (viewMonths(page).includes(month)) {
        showMonth(page, month, 'month');
    }
}

/**
 * Goes to a month (arrows, the month select, today). On a phone the drawn view only scrolls
 * to it — the timeline and the days list hold every month — unless the UTC day has changed
 * since it was drawn; the month grid is drawn anew.
 * @param {Object} page
 * @param {string} month YYYY-MM
 * @param {'today'|'month'} target where the days list scrolls: today or the month's first day
 */
function showMonth(page, month, target) {
    page.state = { ...page.state, month };
    writeState(page.state, page.now);
    if (PHONE.matches && page.drawnDay === todayKey(page.now)) {
        updateMonthControls(page, viewMonths(page));
    } else {
        render(page);
    }
    placeView(page, target);
}

/** Where the days list scrolls after a redraw: today in the current month, else the month. */
function defaultTarget(page) {
    return page.state.month === monthKey(page.now) ? 'today' : 'month';
}

/**
 * One listener for the page's whole life: when the screen crosses the phone width, the
 * calendar on the page now (PJAX swaps it) is drawn again.
 */
function watchWidth() {
    if (watchingWidth) {
        return;
    }
    watchingWidth = true;
    PHONE.addEventListener('change', () => {
        const page = pages.get(document.querySelector('[data-releases-page]'));
        if (!page?.data) {
            return;
        }
        closeTypeSheet(page.root);
        render(page);
        placeView(page, defaultTarget(page));
    });
}

/**
 * Back to the page through PJAX or the back/forward cache: after a failed load — try again;
 * data older than STALE_MS — fetch again (a tab left open showed yesterday's calendar, 04.10);
 * another day (UTC) — today and "now" again.
 */
async function refreshAfterReturn(page) {
    const now = new Date();
    const stale = !page.data || Date.now() - page.loadedAt > STALE_MS;
    if (stale && !(await load(page))) {
        return;
    }
    if (!stale && todayKey(now) === page.drawnDay) {
        return;
    }
    page.now = now;
    render(page);
    placeView(page, defaultTarget(page));
}

/** The month an arrow leads to, only within the months of the view. */
function nextMonth(page, step) {
    const month = shiftMonth(page.state.month, step);
    return viewMonths(page).includes(month) ? month : page.state.month;
}

/**
 * The months of the view, oldest first: the timeline's columns. The month view stops at the
 * current month — it draws releases only, so a later month is always empty; on a phone (the
 * days list) only the months with a release of the type, and the current one.
 * @returns {string[]} YYYY-MM keys
 */
function viewMonths(page) {
    const { data, state, now } = page;
    const months = timelineMonths(data, now);
    if (state.view === 'timeline') {
        return months;
    }
    if (PHONE.matches) {
        const days = dayGroups(data.releases, state.type, daysSpan(data, now));
        return [...new Set(days.map(({ day }) => monthKey(day)))].sort();
    }
    const current = monthKey(now);
    return months.filter((month) => month <= current);
}

function render(page) {
    const months = viewMonths(page);
    keepMonthInView(page, months);
    const { root, state } = page;
    root.dataset.view = state.view;
    root.dataset.type = state.type;
    updateControls(root, state);
    updateMonthControls(page, months);
    page.view = null;
    page.drawnDay = todayKey(page.now);
    let view;
    try {
        view = buildView(page);
    } catch (error) {
        showNote(page, ERROR_NOTE, error);
        return;
    }
    // The days list always has today, so it is never "empty"; the others are without a chip.
    const empty = !view.matches('.th-rel-days') && view.querySelector('.th-rel-chip') === null;
    root.querySelector('[data-releases-body]').replaceChildren(empty ? element('p', 'th-rel-note', EMPTY_NOTE) : view);
    page.view = empty ? null : view;
}

function buildView({ data, state, now }) {
    if (state.view === 'timeline') {
        return buildTimeline(data, state, now);
    }
    return PHONE.matches ? buildDays(data, state, now) : buildMonth(data, state, now);
}

function updateMonthControls(page, months) {
    const { root, state } = page;
    updateArrows(root, state.month, months);
    root.querySelector('[data-releases-month]').textContent = monthLabel(state.month, true);
    fillMonthSelect(root.querySelector('[data-releases-month-select]'), months, state.month);
}

/**
 * Scrolls to the chosen month once the drawn view has its size: the timeline sideways (on a
 * phone the month at the right edge), the days list — the page, to today or to the month's
 * first day. Back through PJAX the days list returns once to the scroll the page had instead.
 * Nothing happens if the view was drawn again or left the page (PJAX) in the meantime.
 * @param {Object} page
 * @param {'today'|'month'} target where the days list scrolls
 */
function placeView(page, target) {
    const { view } = page;
    if (!view) {
        return;
    }
    stylesReady().then(() => {
        if (page.view !== view || !view.isConnected) {
            return;
        }
        if (view.matches('.th-rel-timeline')) {
            const scroll = PHONE.matches ? scrollToMonthEnd : scrollToMonth;
            scroll(view, page.state.month);
        } else if (view.matches('.th-rel-days')) {
            placeDays(page, target);
        }
    });
}

function placeDays(page, target) {
    const { restoreY } = page;
    page.restoreY = undefined;
    if (Number.isFinite(restoreY)) {
        window.scrollTo(0, restoreY);
        return;
    }
    const header = stuckHeaderHeight();
    if (target === 'today') {
        // Today heads the list: on a fresh page it is already in view under the title.
        const today = page.view.querySelector('[data-today]');
        const { top } = today.getBoundingClientRect();
        if (top < header || top > window.innerHeight / 2) {
            scrollPageTo(today, header + MONTH_TOP_GAP);
        }
        return;
    }

    scrollPageTo(monthStart(page.view, page.state.month), header + MONTH_TOP_GAP);
}

/**
 * The chosen month is one of the view's months: a month from the address or from the other
 * view that is not among them becomes the next one that is, or the last (the address follows).
 */
function keepMonthInView(page, months) {
    const { month } = page.state;
    if (months.includes(month)) {
        return;
    }
    const next = months.find((key) => key > month) || months[months.length - 1];
    page.state = { ...page.state, month: next };
    writeState(page.state, page.now);
}

/**
 * The calendar's own styles (releases.css, linked in the page body): after a PJAX swap they
 * may still be loading, and the timeline can be measured only once its columns have widths.
 * @returns {Promise<void>}
 */
function stylesReady() {
    const link = document.querySelector('link[href*="releases.css"]');
    if (!link || link.sheet) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        link.addEventListener('load', () => resolve(), { once: true });
        link.addEventListener('error', () => resolve(), { once: true });
    });
}

function showNote(page, text, error) {
    console.warn('[releases] calendar not shown', error);
    page.root.querySelector('[data-releases-body]').replaceChildren(element('p', 'th-rel-note', text));
}

/** View and type buttons, made once: re-rendering only updates them, so focus stays. */
function buildControls(page) {
    const counts = countByType(page.data.releases);
    page.root.querySelector('[data-releases-types]').replaceChildren(...TYPES.map((type) => {
        const button = setButton('th-rel-type', `type:${type}`);
        button.append(element('span', '', capitalize(type)), element('span', 'th-rel-type__count', String(counts[type] || 0)));
        return button;
    }));
    page.root.querySelector('[data-releases-views]').replaceChildren(...VIEWS.map((view) => {
        const button = setButton('th-rel-view', `view:${view}`);
        button.textContent = capitalize(view);
        return button;
    }));
}

/** Every view and type button — above the calendar, in the dock, in the type sheet. */
function updateControls(root, state) {
    root.querySelectorAll('[data-set^="view:"], [data-set^="type:"]').forEach((button) => {
        const [field, value] = button.dataset.set.split(':');
        button.setAttribute('aria-pressed', String(state[field] === value));
    });
    // In the "Showing text models" line the type stays lowercase; a standalone label
    // (the phone dock) starts with a capital.
    root.querySelectorAll('[data-releases-type-label]').forEach((label) => {
        label.textContent = label.dataset.releasesTypeLabel === 'standalone' ? capitalize(state.type) : state.type;
    });
}


/** An arrow at the edge of the view's months is disabled: there is nothing past it. */
function updateArrows(root, month, months) {
    root.querySelector('[data-set="month:-1"]').disabled = month === months[0];
    root.querySelector('[data-set="month:1"]').disabled = month === months[months.length - 1];
}

function setButton(className, value) {
    const button = element('button', className);
    button.type = 'button';
    button.dataset.set = value;
    return button;
}

/** Window content for a [data-pop] key: release, expected release or "more". */
function details(page, key) {
    const [kind, id, extra] = key.split(':');
    const studios = new Map(page.data.studios.map((studio) => [studio.id, studio]));
    if (kind === 'release') {
        return releaseWindow(page, studios, id);
    }
    if (kind === 'studio') {
        return studios.get(id)?.expected ? expectedDetails(studios.get(id)) : null;
    }
    if (kind === 'more') {
        return chipList(cellChips(page.data, page, { studio: id, key: extra }));
    }
    return chipList(page.data.releases
        .filter((release) => release.type === page.state.type && release.date === id)
        .map((release) => modelChip(release, studios.get(release.studio))));
}

function releaseWindow(page, studios, id) {
    const release = page.data.releases.find((item) => String(item.id) === id);
    if (!release) {
        return null;
    }
    const context = { now: page.now, attribution: page.data.attribution };
    return releaseDetails(release, studios.get(release.studio), context);
}

/** The "+N more" window: every chip of the cell or day. */
function chipList(chips) {
    const list = element('div', 'th-rel-pop th-rel-pop--list');
    list.append(...chips);
    return list;
}
